import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSingleGame } from '../hooks/useSingleGame';
import { useSound } from '../hooks/useSound';
import type { SingleEvent } from '../hooks/useSingleGame';
import type { Direction } from '../utils/gameLogic';
import '../styles/single.css';

const KEY_MAP: Record<string, Direction> = {
  ArrowUp: 'up', ArrowDown: 'down', ArrowLeft: 'left', ArrowRight: 'right',
  w: 'up', W: 'up', s: 'down', S: 'down', a: 'left', A: 'left', d: 'right', D: 'right',
};

export const SingleGameBoard: React.FC<{ onBack: () => void }> = ({ onBack }) => {
  const sound = useSound();
  const soundRef = useRef(sound);
  useEffect(() => { soundRef.current = sound; }, [sound]);

  const onEvent = useCallback((e: SingleEvent) => {
    const s = soundRef.current;
    switch (e.type) {
      case 'move': e.merged ? s.playMerge() : s.playMove(); break;
      case 'win': s.playVictory(); break;
      case 'over': s.playDefeat(); break;
    }
  }, []);

  const { board, tiles, score, bestScore, status, newGame, continueAfterWin, move } = useSingleGame(onEvent);
  const size = board.length;

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const dir = KEY_MAP[e.key];
      if (dir) { e.preventDefault(); move(dir); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [move]);

  const { cellSize, gap } = useMemo(() => {
    const cs = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--sp-cell-size')?.trim() || '100') || 100;
    const g = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--sp-cell-gap')?.trim() || '10') || 10;
    return { cellSize: cs, gap: g };
  }, []);

  const getTileClass = (value: number): string => value >= 4096 ? 'tile-super' : `tile-${value}`;

  const [scorePop, setScorePop] = useState<{ value: number; key: number } | null>(null);
  const prevScore = useRef(score);
  const popKey = useRef(0);

  useEffect(() => {
    const delta = score - prevScore.current;
    if (delta > 0) {
      popKey.current++;
      setScorePop({ value: delta, key: popKey.current });
      const t = setTimeout(() => setScorePop(null), 800);
      prevScore.current = score;
      return () => clearTimeout(t);
    }
    prevScore.current = score;
  }, [score]);

  return (
    <div className="single-game">
      <div className="sp-header">
        <div className="sp-title-row">
          <button className="sp-back" onClick={onBack}>← 菜单</button>
          <h1 className="sp-title">2048</h1>
          <button className="sp-new-game" onClick={newGame}>新游戏</button>
        </div>

        <div className="sp-scores">
          <div className="sp-score-box" style={{ position: 'relative' }}>
            <span className="sp-score-label">分数</span>
            <span className="sp-score-value">{score}</span>
            {scorePop && <span className="sp-score-pop" key={scorePop.key}>+{scorePop.value}</span>}
          </div>
          <div className="sp-score-box">
            <span className="sp-score-label">最佳</span>
            <span className="sp-score-value">{bestScore}</span>
          </div>
        </div>
      </div>

      <div className="sp-board-wrapper">
        <div
          className="board-grid"
          style={{
            gridTemplateColumns: `repeat(${size}, var(--sp-cell-size))`,
            gridTemplateRows: `repeat(${size}, var(--sp-cell-size))`,
            gap: 'var(--sp-cell-gap)',
            padding: 'var(--sp-cell-gap)',
          }}
        >
          {Array.from({ length: size * size }, (_, i) => (
            <div key={i} className="board-cell sp-cell" />
          ))}
        </div>

        <div className="tiles-container" style={{ padding: 'var(--sp-cell-gap)' }}>
          {tiles.map(tile => {
            const x = tile.col * (cellSize + gap);
            const y = tile.row * (cellSize + gap);
            const cls = ['tile', 'sp-tile', getTileClass(tile.value)];
            if (tile.isNew) cls.push('tile-new');
            if (tile.isMerged) cls.push('tile-merged');

            return (
              <div
                key={tile.id}
                className={cls.join(' ')}
                style={{
                  '--tile-pos': `translate(${x}px, ${y}px)`,
                  transform: `translate(${x}px, ${y}px)`,
                  width: `${cellSize}px`,
                  height: `${cellSize}px`,
                } as React.CSSProperties}
              >
                {tile.value}
              </div>
            );
          })}
        </div>

        {status === 'won' && (
          <div className="sp-overlay sp-win">
            <div className="sp-overlay-content">
              <h2>You Win!</h2>
              <p>你达到了 2048!</p>
              <div className="sp-overlay-buttons">
                <button className="btn-primary" onClick={continueAfterWin}>继续挑战</button>
                <button className="btn-secondary" onClick={newGame}>新游戏</button>
              </div>
            </div>
          </div>
        )}

        {status === 'over' && (
          <div className="sp-overlay sp-gameover">
            <div className="sp-overlay-content">
              <h2>Game Over</h2>
              <p>最终分数: {score}</p>
              <div className="sp-overlay-buttons">
                <button className="btn-primary" onClick={newGame}>再来一局</button>
                <button className="btn-secondary" onClick={onBack}>返回菜单</button>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="sp-hint">
        使用 <strong>方向键</strong> 或 <strong>WASD</strong> 控制方块移动
      </div>
    </div>
  );
};

export default SingleGameBoard;
